/**
 * Workflow to Graph Conversion
 *
 * Converts WorkflowAgent API data to React Flow graph format.
 * This is a pure function - the caller is responsible for memoization if needed.
 */

import {
  ConditionalStep,
  SpecializedAgentStep,
  Step,
  StepType,
  TriggerType,
  WorkflowAgent,
} from '@frontend/types/workflow-agent';

import {
  ConditionBranchNode,
  ConditionEdge,
  ConditionNodeData,
  getConditionEdgeLabel,
  StepNode,
  StepWithPosition,
  TriggerNode,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
} from '../types';
import { EdgeMetadata, NodeMetadata, WorkflowMetadata } from '../types/api';

import {
  generateConditionNodeId,
  generateEdgeId,
  generateLegacyConditionNodeId,
} from './workflow-id-utils';

/**
 * Permission context for determining node deletability
 */
export type WorkflowPermissions = {
  canManageWorkflowAgents: boolean;
  canManageWorkflowTriggers: boolean;
};

/**
 * Create a standard workflow edge.
 */
function createStandardEdge(
  id: string,
  source: string,
  target: string,
  sourceHandle?: string | null,
  targetHandle?: string | null
): WorkflowEdge {
  return {
    id,
    source,
    target,
    sourceHandle: sourceHandle ?? undefined,
    targetHandle: targetHandle ?? undefined,
    deletable: true,
    type: 'custom',
  };
}

/**
 * Minimal agent info needed to render an organic agent node (resolved from the
 * instance's specialized-agent list by `agent_id`).
 */
export type OrganicAgentInfo = {
  name: string;
  description?: string | null;
  is_system_agent?: boolean;
  links?: SpecializedAgentStep['links'];
};

/**
 * Convert an organic workflow agent to React Flow graph format.
 *
 * Organic agents have no authored steps or edges: the canvas shows the trigger nodes
 * plus one specialized-agent node per `agent_choice` id (the eligible agent set).
 * Positions are restored from `agent_metadata` (agent nodes are keyed by `agent_id`).
 * `resolveAgent` supplies the display name/description for each agent id; an unknown id
 * (e.g. a deleted agent) still renders a placeholder node so the author sees it.
 */
export function convertOrganicWorkflowToGraph(
  agentChoice: string[] | null | undefined,
  triggers: WorkflowAgent['triggers'],
  metadata: WorkflowMetadata,
  resolveAgent: (agentId: string) => OrganicAgentInfo | undefined,
  permissions?: WorkflowPermissions
): WorkflowGraph {
  const getMetadataNode = (id: string): NodeMetadata | undefined =>
    metadata.nodes.find(node => node.id === id);

  const nodes: WorkflowNode[] = [];
  // Organic agents never have edges — the planner decides step order at runtime.
  const edges: WorkflowEdge[] = [];

  (triggers ?? []).forEach(trigger => {
    const triggerMetadata = getMetadataNode(trigger.id);
    let deletable = true;
    if (permissions) {
      deletable =
        trigger.type === TriggerType.MESSAGE
          ? permissions.canManageWorkflowAgents
          : permissions.canManageWorkflowTriggers;
    }
    nodes.push({
      id: trigger.id,
      type: trigger.type,
      position: triggerMetadata?.position || { x: 0, y: 0 },
      data: trigger,
      measured: triggerMetadata?.measured,
      deletable,
    } as TriggerNode);
  });

  (agentChoice ?? []).forEach((agentId, index) => {
    const agentMetadata = getMetadataNode(agentId);
    const info = resolveAgent(agentId);
    const position = agentMetadata?.position || { x: 320, y: 120 + index * 140 };
    nodes.push({
      id: agentId,
      type: StepType.SPECIALIZED,
      position,
      measured: agentMetadata?.measured,
      data: {
        type: StepType.SPECIALIZED,
        step_id: agentId,
        agent_id: agentId,
        name: info?.name ?? agentId,
        description: info?.description ?? '',
        is_system_agent: info?.is_system_agent ?? false,
        links: info?.links,
        next_step_id: null,
        parameters: {},
        timeout_seconds: 3600,
      },
    } as StepNode);
  });

  return { nodes, edges };
}

/**
 * Convert workflow agent API data to React Flow graph format.
 *
 * @param steps - The steps from the workflow agent
 * @param triggers - The triggers from the workflow agent
 * @param startStepId - The ID of the starting step
 * @param metadata - The workflow metadata containing node positions and edge info
 * @param permissions - Optional permissions context for determining node deletability
 * @returns A WorkflowGraph with typed nodes and edges
 */
export function convertWorkflowToGraph(
  steps: Record<string, Step>,
  triggers: WorkflowAgent['triggers'],
  startStepId: string,
  metadata: WorkflowMetadata,
  permissions?: WorkflowPermissions,
  options?: { legacyConditionRendering?: boolean }
): WorkflowGraph {
  // Metadata lookup functions
  const getMetadataNode = (nodeId: string): NodeMetadata | undefined => {
    return metadata.nodes.find(node => node.id === nodeId);
  };

  const getMetadataEdge = (source: string, target: string): EdgeMetadata | undefined => {
    return metadata.edges?.find(edge => edge.source === source && edge.target === target);
  };

  /**
   * Create trigger nodes from workflow triggers.
   */
  function createTriggerNodes(workflowTriggers: WorkflowAgent['triggers']): TriggerNode[] {
    const triggerNodes: TriggerNode[] = [];

    if (!workflowTriggers || workflowTriggers.length === 0) {
      return triggerNodes;
    }

    workflowTriggers.forEach(trigger => {
      const triggerNodeId = trigger.id;
      const triggerMetadata = getMetadataNode(triggerNodeId);

      // Determine if this trigger node is deletable based on permissions
      let deletable = true;
      if (permissions) {
        if (trigger.type === TriggerType.MESSAGE) {
          // Message triggers: require manage_workflow_agents permission
          deletable = permissions.canManageWorkflowAgents;
        } else {
          // Non-message triggers: require manage_workflow_triggers permission
          deletable = permissions.canManageWorkflowTriggers;
        }
      }

      // Build trigger node - cast needed because object literal with dynamic type
      // doesn't preserve discriminated union narrowing
      const triggerNode = {
        id: triggerNodeId,
        type: trigger.type,
        position: triggerMetadata?.position || { x: 0, y: 0 },
        data: trigger,
        measured: triggerMetadata?.measured,
        deletable,
      } as TriggerNode;

      triggerNodes.push(triggerNode);
    });

    return triggerNodes;
  }

  /**
   * Create a step node from step data.
   */
  function createStepNode(step: Step, parentId?: string): StepNode {
    const metadataNode = getMetadataNode(step.step_id);

    // Check if step has embedded position (from ForLoop steps)
    let position = metadataNode?.position || { x: 0, y: 0 };

    // Handle position that might be embedded in step data (StepWithPosition)
    const stepWithPosition = step as StepWithPosition;
    if (stepWithPosition.position) {
      const { x, y } = stepWithPosition.position;
      if (x !== undefined && y !== undefined) {
        position = { x, y };
      }
    }

    // Default position for nested nodes
    if (parentId && position.x === 0 && position.y === 0) {
      position = { x: 40, y: 60 };
    }

    // Build the node - cast needed because object literal with dynamic type
    // doesn't preserve discriminated union narrowing
    const stepNode = {
      id: step.step_id,
      type: step.type,
      position,
      data: step,
      parentId,
      extent: parentId ? ('parent' as const) : undefined,
      measured: metadataNode?.measured,
    } as StepNode;

    // Handle ForLoop node dimensions
    if (step.type === StepType.FOR_LOOP) {
      const savedWidth = metadataNode?.measured?.width || metadataNode?.width;
      const savedHeight = metadataNode?.measured?.height || metadataNode?.height;

      const width = savedWidth || (parentId ? 280 : 320);
      const height = savedHeight || (parentId ? 160 : 188);

      stepNode.style = { width, height };
      stepNode.width = width;
      stepNode.height = height;
    }

    return stepNode;
  }

  /**
   * Determine whether to use legacy (N-node) condition rendering for a step.
   * Returns true only when legacyConditionRendering is enabled AND old hash-based
   * metadata exists (but new-format metadata does not).
   */
  function shouldUseLegacyConditionRendering(step: ConditionalStep): boolean {
    if (!options?.legacyConditionRendering) return false;
    if (!step.conditions || step.conditions.length === 0) return false;

    // If new-format metadata exists, use new rendering
    const newFormatId = generateConditionNodeId(step.step_id);
    if (getMetadataNode(newFormatId)) return false;

    // Check if old hash-based metadata exists for any condition
    return step.conditions.some(condition => {
      const oldId = generateLegacyConditionNodeId(step.step_id, condition);
      return !!getMetadataNode(oldId);
    });
  }

  /**
   * Create condition nodes for a conditional step.
   * Dispatches to legacy or new rendering based on metadata format.
   */
  function createConditionalStepNodes(
    step: ConditionalStep,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    parentId?: string
  ): void {
    if (shouldUseLegacyConditionRendering(step)) {
      createLegacyConditionalStepNodes(step, nodes, edges, parentId);
    } else {
      createNewConditionalStepNodes(step, nodes, edges, parentId);
    }
  }

  /**
   * New rendering: one condition node with multiple outgoing ConditionEdge instances.
   */
  function createNewConditionalStepNodes(
    step: ConditionalStep,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    parentId?: string
  ): void {
    const conditionNodeId = generateConditionNodeId(step.step_id);
    const conditionMetadata = getMetadataNode(conditionNodeId);

    // When no new-format metadata exists, try to derive position from old condition nodes
    let fallbackPosition = { x: 0, y: 0 };
    if (!conditionMetadata) {
      if (step.conditions.length > 0) {
        const oldPositions = step.conditions
          .map(c => getMetadataNode(generateLegacyConditionNodeId(step.step_id, c))?.position)
          .filter((p): p is { x: number; y: number } => !!p);
        if (oldPositions.length > 0) {
          oldPositions.sort((a, b) => a.y - b.y || a.x - b.x);
          fallbackPosition = oldPositions[Math.floor(oldPositions.length / 2)];
        }
      }
      if (fallbackPosition.x === 0 && fallbackPosition.y === 0) {
        const parentMeta = getMetadataNode(step.step_id);
        if (parentMeta?.position) {
          fallbackPosition = {
            x: parentMeta.position.x,
            y: parentMeta.position.y + 200,
          };
        }
      }
    }

    const conditionNode: ConditionBranchNode = {
      id: conditionNodeId,
      type: 'condition',
      position: conditionMetadata?.position || fallbackPosition,
      data: { type: 'condition', name: 'Condition', parent_step_id: step.step_id },
      parentId,
      extent: parentId ? 'parent' : undefined,
      measured: conditionMetadata?.measured,
    };

    nodes.push(conditionNode);

    const edgeToCondition = getMetadataEdge(step.step_id, conditionNodeId);
    edges.push(
      createStandardEdge(
        edgeToCondition?.id || generateEdgeId(),
        step.step_id,
        conditionNodeId,
        edgeToCondition?.sourceHandle,
        edgeToCondition?.targetHandle
      )
    );

    step.conditions.forEach(condition => {
      const edgeMetadata = getMetadataEdge(conditionNodeId, condition.go_to_step_id);

      const conditionEdge: ConditionEdge = {
        id: edgeMetadata?.id || generateEdgeId(),
        source: conditionNodeId,
        target: condition.go_to_step_id,
        type: 'condition',
        data: {
          id: condition.id,
          matching_condition: condition.matching_condition,
          match_type: condition.match_type,
          case_sensitive: condition.case_sensitive ?? true,
        },
        sourceHandle: edgeMetadata?.sourceHandle ?? undefined,
        targetHandle: edgeMetadata?.targetHandle ?? undefined,
        label: getConditionEdgeLabel({
          matching_condition: condition.matching_condition,
          match_type: condition.match_type,
        }),
      };

      edges.push(conditionEdge);
    });
  }

  /**
   * Legacy rendering: N condition nodes (one per condition), each with condition
   * data on node.data. Used for backward-compatible display of old executions.
   */
  function createLegacyConditionalStepNodes(
    step: ConditionalStep,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    parentId?: string
  ): void {
    step.conditions.forEach(condition => {
      const conditionNodeId = generateLegacyConditionNodeId(step.step_id, condition);
      const conditionMetadata = getMetadataNode(conditionNodeId);

      const conditionData: ConditionNodeData = {
        type: 'condition',
        name: `Condition ${condition.match_type}`,
        parent_step_id: step.step_id,
        matching_condition: condition.matching_condition,
        match_type: condition.match_type,
        label: `${condition.match_type} ${condition.matching_condition}`,
      };

      const conditionNode: ConditionBranchNode = {
        id: conditionNodeId,
        type: 'condition',
        position: conditionMetadata?.position || { x: 0, y: 0 },
        data: conditionData,
        parentId,
        extent: parentId ? 'parent' : undefined,
        measured: conditionMetadata?.measured,
      };

      nodes.push(conditionNode);

      const edgeToCondition = getMetadataEdge(step.step_id, conditionNodeId);
      edges.push(
        createStandardEdge(
          edgeToCondition?.id || generateEdgeId(),
          step.step_id,
          conditionNodeId,
          edgeToCondition?.sourceHandle,
          edgeToCondition?.targetHandle
        )
      );

      const edgeFromCondition = getMetadataEdge(conditionNodeId, condition.go_to_step_id);
      edges.push(
        createStandardEdge(
          edgeFromCondition?.id || generateEdgeId(),
          conditionNodeId,
          condition.go_to_step_id,
          edgeFromCondition?.sourceHandle,
          edgeFromCondition?.targetHandle
        )
      );
    });
  }

  /**
   * Process steps recursively, creating nodes and edges.
   */
  function processSteps(
    stepsToProcess: Record<string, Step>,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    parentId?: string
  ): void {
    if (!stepsToProcess || Object.keys(stepsToProcess).length === 0) {
      return;
    }

    // Create nodes for all steps
    Object.values(stepsToProcess).forEach(step => {
      const stepNode = createStepNode(step, parentId);
      nodes.push(stepNode);

      // Handle conditional steps - create condition nodes
      if (step.type === StepType.CONDITIONAL) {
        createConditionalStepNodes(step, nodes, edges, parentId);
      }

      // Handle for-loop steps - process nested steps recursively
      if (step.type === StepType.FOR_LOOP) {
        const forLoopSteps = step.steps || {};
        processSteps(forLoopSteps, nodes, edges, step.step_id);
      }
    });

    // Create edges between steps based on next_step_id
    Object.values(stepsToProcess).forEach(step => {
      if (step.next_step_id && step.type !== StepType.CONDITIONAL) {
        if (stepsToProcess[step.next_step_id]) {
          const metadataEdge = getMetadataEdge(step.step_id, step.next_step_id);
          edges.push(
            createStandardEdge(
              metadataEdge?.id || generateEdgeId(),
              step.step_id,
              step.next_step_id,
              metadataEdge?.sourceHandle,
              metadataEdge?.targetHandle
            )
          );
        }
      }
    });
  }

  /**
   * Create edges from triggers to the start step.
   */
  function createTriggerToStartStepEdges(
    workflowTriggers: WorkflowAgent['triggers'],
    startId: string | undefined,
    edges: WorkflowEdge[]
  ): void {
    if (!startId || !workflowTriggers || workflowTriggers.length === 0) {
      return;
    }

    workflowTriggers.forEach(trigger => {
      const metadataEdge = getMetadataEdge(trigger.id, startId);
      edges.push(
        createStandardEdge(
          metadataEdge?.id || generateEdgeId(),
          trigger.id,
          startId,
          metadataEdge?.sourceHandle,
          metadataEdge?.targetHandle
        )
      );
    });
  }

  // Main conversion logic
  const nodes: WorkflowNode[] = [];
  const edges: WorkflowEdge[] = [];

  if (!steps || !triggers) {
    return { nodes, edges };
  }

  // Create trigger nodes
  const triggerNodes = createTriggerNodes(triggers);
  nodes.push(...triggerNodes);

  // Process all steps recursively
  processSteps(steps, nodes, edges);

  // Create edges from triggers to start step
  createTriggerToStartStepEdges(triggers, startStepId, edges);

  return { nodes, edges };
}
